/**
 * Cortado's template-authoring vocabulary is nested dot-paths,
 * `{{ .entity.contact.first_name }}` (dot-separated identifiers, optional
 * inner whitespace). Previously-saved rows may also carry pre-migration flat
 * PascalCase tokens (`{{.CustomerFirstName}}`), and `system_template_overrides.go`
 * (a separate, unrelated engine) builds its own ad-hoc flat tokens with
 * identical `{{.X}}` syntax. Every surface that recognizes or extracts
 * template variables — the variable picker, the compose editor, the rich-text
 * decoration layer — must accept all of the above, so this module is the
 * single place that defines the token grammar. Matching only
 * `[A-Za-z0-9_]` (no dots) is what made nested-path templates invisible to
 * the picker, the preview, and the used-variable extraction that feeds save.
 *
 * Regexes built with the `g` flag carry mutable `lastIndex` state, so every
 * export that hands one out is a factory (`createX()`) returning a fresh
 * instance — never a shared module-level constant — so repeated or
 * interleaved callers can't interfere with each other.
 */
const VARIABLE_PATH = String.raw`[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*`

// Rich-text HTML can preserve a visually ordinary space as an encoded
// non-breaking space. The preview scans the serialized HTML (before the
// browser decodes entities), so `\s` alone misses tokens such as
// `{{&nbsp;.entity.contact.first_name&nbsp;}}` even though they look identical
// to a token with regular spaces on screen.
const VARIABLE_PADDING = String.raw`(?:\s|&nbsp;|&#160;|&#[xX][aA]0;)*`

/**
 * Anchored, non-global — matches a string that is EXACTLY one token. Safe to
 * share as a single instance: without the `g` flag, `RegExp.prototype.match`
 * never touches `lastIndex`.
 */
const anchoredTemplateVariablePattern = new RegExp(
  String.raw`^\{\{${VARIABLE_PADDING}\.(${VARIABLE_PATH})${VARIABLE_PADDING}\}\}$`
)

/** Global (non-anchored) matcher for finding tokens inside a larger string. */
export function createTemplateVariableMatcher(): RegExp {
  return new RegExp(
    String.raw`\{\{${VARIABLE_PADDING}\.(${VARIABLE_PATH})${VARIABLE_PADDING}\}\}`,
    'g'
  )
}

/** Same as above, but capturing the whole token — for String.split() highlighting. */
export function createTemplateVariableSplitter(): RegExp {
  return new RegExp(
    String.raw`(\{\{${VARIABLE_PADDING}\.${VARIABLE_PATH}${VARIABLE_PADDING}\}\})`,
    'g'
  )
}

/** True when the token uses nested dot-path (workflow engine) syntax. */
export function isDottedVariablePath(name: string): boolean {
  return name.includes('.')
}

/**
 * Returns the variable name when `part` is EXACTLY one token, else null.
 *
 * Use this (not getTemplateVariableName) when classifying the pieces produced
 * by splitting text on createTemplateVariableSplitter: a non-token piece can
 * still begin with "{{" (e.g. "{{foo}} hi"), and must not be styled as a
 * variable.
 */
export function matchTemplateVariableToken(part: string): string | null {
  return part.trim().match(anchoredTemplateVariablePattern)?.[1] ?? null
}

/**
 * Accepts either a bare name (`CustomerFirstName`, `entity.contact.first_name`)
 * or a full token (`{{.CustomerFirstName}}`) and returns the bare name. Stored
 * `available_variables` uses both shapes: system templates persist tokens with
 * braces, workflow templates persist bare dot-paths.
 */
export function getTemplateVariableName(variable: string): string {
  const trimmed = variable.trim()
  const match = trimmed.match(anchoredTemplateVariablePattern)
  if (match) return match[1]
  // Strip only braces/whitespace — dots are significant in workflow paths.
  return trimmed.replace(/[{}]/g, '').replace(/\s/g, '').replace(/^\./, '')
}

export function toTemplateVariableSyntax(variable: string): string {
  const name = getTemplateVariableName(variable)
  // Dotted paths get spaces inside the braces to match how the backend's stock
  // workflow templates are authored ("{{ .entity.contact.first_name }}").
  return isDottedVariablePath(name) ? `{{ .${name} }}` : `{{.${name}}}`
}

export interface TemplateVariableTokenSpan {
  /** Index of the token's opening `{`. */
  start: number
  /** Index just past the token's closing `}`. */
  end: number
  /** Bare variable name, e.g. `entity.contact.first_name`. */
  name: string
}

/**
 * Finds the token a caret at `offset` is "inside" for Backspace purposes: the
 * caret must sit strictly after the token's first character and no further than
 * its last. A caret at `start` is *before* the token (Backspace there belongs to
 * the preceding character), so it returns null.
 *
 * Tokens render as atomic-looking chips but are plain text in the document, so
 * editors use this to delete a whole token in one keystroke instead of leaving
 * a broken half-token behind.
 */
export function findTemplateVariableTokenAtOffset(
  text: string,
  offset: number
): TemplateVariableTokenSpan | null {
  for (const match of text.matchAll(createTemplateVariableMatcher())) {
    if (match.index === undefined) continue

    const start = match.index
    if (start >= offset) break

    const end = start + match[0].length
    if (offset <= end) return { start, end, name: match[1] }
  }

  return null
}

/** Extract every distinct variable token in `text`, normalized to display syntax, sorted. */
export function extractTemplateVariables(text: string): string[] {
  const variables = new Set<string>()
  for (const match of text.matchAll(createTemplateVariableMatcher())) {
    variables.add(toTemplateVariableSyntax(match[1]))
  }
  return Array.from(variables).sort()
}
